import React from 'react';
import { Outlet, Link, NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Package, Tag, ShoppingBag, Users, Ticket,
  Settings, CreditCard, Landmark, FileText, Star, LogOut, Globe, Shield,
  BarChart2, Image, Megaphone, Bell, Menu, FolderOpen, Activity,
  Mail, MessageSquare, Truck, ChevronDown, ChevronRight, Download, Phone
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { apiService } from '../api/client';

interface NavGroup {
  label: string;
  items: { label: string; href: string; icon: React.ReactNode }[];
}

const navGroups: NavGroup[] = [
  {
    label: 'Genel',
    items: [
      { label: 'Dashboard', href: '/admin', icon: <LayoutDashboard size={16} /> },
      { label: 'Analitik', href: '/admin/analitik', icon: <BarChart2 size={16} /> },
      { label: 'Bildirimler', href: '/admin/bildirimler', icon: <Bell size={16} /> },
    ],
  },
  {
    label: 'Katalog',
    items: [
      { label: 'Ürünler', href: '/admin/urunler', icon: <Package size={16} /> },
      { label: 'Kategoriler', href: '/admin/kategoriler', icon: <Tag size={16} /> },
      { label: 'Markalar', href: '/admin/markalar', icon: <Star size={16} /> },
      { label: 'Medya', href: '/admin/medya', icon: <Image size={16} /> },
    ],
  },
  {
    label: 'Satış',
    items: [
      { label: 'Siparişler', href: '/admin/siparisler', icon: <ShoppingBag size={16} /> },
      { label: 'Faturalar', href: '/admin/faturalar', icon: <FileText size={16} /> },
      { label: 'Ödemeler', href: '/admin/odemeler', icon: <CreditCard size={16} /> },
      { label: 'EFT Dekontları', href: '/admin/dekontlar', icon: <Download size={16} /> },
      { label: 'Müşteriler', href: '/admin/musteriler', icon: <Users size={16} /> },
      { label: 'Kuponlar', href: '/admin/kuponlar', icon: <Ticket size={16} /> },
    ],
  },
  {
    label: 'Pazarlama',
    items: [
      { label: 'Kampanyalar', href: '/admin/kampanyalar', icon: <Megaphone size={16} /> },
      { label: 'Bannerlar', href: '/admin/bannerlar', icon: <Image size={16} /> },
      { label: 'Blog', href: '/admin/blog', icon: <FileText size={16} /> },
      { label: 'SEO', href: '/admin/seo', icon: <Globe size={16} /> },
    ],
  },
  {
    label: 'Destek',
    items: [
      { label: 'Canlı Destek', href: '/admin/canli-destek', icon: <MessageSquare size={16} /> },
      { label: 'Destek Talepleri', href: '/admin/destek', icon: <Users size={16} /> },
      { label: 'Yorumlar', href: '/admin/yorumlar', icon: <Star size={16} /> },
    ],
  },
  {
    label: 'Site Yönetimi',
    items: [
      { label: 'Menü Yöneticisi', href: '/admin/menu-yoneticisi', icon: <Menu size={16} /> },
      { label: 'Statik Sayfalar', href: '/admin/sayfalar', icon: <FolderOpen size={16} /> },
    ],
  },
  {
    label: 'Sistem',
    items: [
      { label: 'Roller & İzinler', href: '/admin/roller', icon: <Shield size={16} /> },
      { label: 'Aktivite Günlükleri', href: '/admin/aktivite-gunlukleri', icon: <Activity size={16} /> },
      { label: 'Kargo Yönetimi', href: '/admin/kargo', icon: <Truck size={16} /> },
      { label: 'Ödeme Kuralları', href: '/admin/odeme-kurallari', icon: <CreditCard size={16} /> },
      { label: 'Banka Hesapları', href: '/admin/banka-hesaplari', icon: <Landmark size={16} /> },
      { label: 'E-posta Ayarları', href: '/admin/email-ayarlari', icon: <Mail size={16} /> },
      { label: 'SMS Ayarları', href: '/admin/sms-ayarlari', icon: <MessageSquare size={16} /> },
      { label: 'İletişim Bilgileri', href: '/admin/iletisim-ayarlari', icon: <Phone size={16} /> },
      { label: 'İçe/Dışa Aktar', href: '/admin/import-export', icon: <FileText size={16} /> },
      { label: 'Genel Ayarlar', href: '/admin/ayarlar', icon: <Settings size={16} /> },
    ],
  },
];

const NavGroupSection: React.FC<{ group: NavGroup }> = ({ group }) => {
  const [open, setOpen] = React.useState(true);
  return (
    <div className="mb-1">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-1.5 text-xs font-semibold text-white/40 uppercase tracking-wider hover:text-white/60 transition-colors"
      >
        {group.label}
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && group.items.map(item => (
        <NavLink
          key={item.href}
          to={item.href}
          end={item.href === '/admin'}
          className={({ isActive }) =>
            `flex items-center gap-2.5 px-5 py-2 text-sm transition-colors ${
              isActive
                ? 'bg-cyan-600 text-white font-medium'
                : 'text-white/65 hover:text-white hover:bg-white/8'
            }`
          }
        >
          {item.icon}
          {item.label}
        </NavLink>
      ))}
    </div>
  );
};

const AdminLayout: React.FC = () => {
  const { logout, user } = useAuthStore();

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside className="w-60 bg-[#1a1f2e] text-white flex flex-col shadow-xl flex-shrink-0 overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
          <Link to="/" className="font-bold text-lg tracking-tight hover:text-cyan-400 transition-colors">
            AR Klima
          </Link>
          <span className="text-xs bg-cyan-600 px-1.5 py-0.5 rounded text-white">Admin</span>
        </div>

        <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin scrollbar-thumb-white/10">
          {navGroups.map(group => (
            <NavGroupSection key={group.label} group={group} />
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-2 truncate">{user?.email || 'admin@arklima.com'}</div>
          <button
            onClick={async () => {
              try {
                await apiService.logout();
              } catch (e) {
                // ignore error, clear local state anyway
              }
              logout();
              window.location.href = '/giris';
            }}
            className="w-full flex items-center justify-center gap-2 p-2 mt-4 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white transition-all duration-300 shadow-sm"
          >
            <LogOut size={15} />
            <span className="font-medium">Çıkış Yap</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-auto">
        <header className="bg-white shadow-sm px-8 py-3.5 flex items-center justify-between sticky top-0 z-10">
          <div className="text-sm text-gray-500">AR Klima Yönetim Paneli</div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-xs text-cyan-600 hover:underline flex items-center gap-1">
              <Globe size={14} /> Siteyi Gör
            </a>
            <NavLink to="/admin/bildirimler" className="relative text-gray-500 hover:text-gray-700">
              <Bell size={18} />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">3</span>
            </NavLink>
            <div className="w-8 h-8 rounded-full bg-cyan-600 flex items-center justify-center text-white text-sm font-bold">
              {user?.firstName?.[0] || 'A'}
            </div>
          </div>
        </header>

        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
